import type { ComponentType } from '@grafikr/components';
import type { ProductMedia as Media } from '@grafikr/shopify-typescript/type/liquid/ProductMedia';
import type SwiperType from 'swiper';

import clear from 'utils/clear';
import { image as renderImage } from 'utils/image';

const component: ComponentType = (node, { emitter }) => {
  const labels = node.querySelector<HTMLElement>('[data-sticky-label]');
  const form = document.getElementById('product-info');

  if (labels && form && window.matchMedia('(min-width: 1024px)').matches) {
    labels.style.height = `${form.offsetHeight}px`;

    const kiwi = document.getElementById('KiwiSizingChart');

    if (kiwi) {
      const observer = new MutationObserver((mutationList, obs) => {
        mutationList.forEach((mutation) => {
          if (
            mutation.addedNodes.length &&
            (mutation.addedNodes[0] as HTMLElement).classList?.contains(
              'ks-calculator-page-basic-info',
            )
          ) {
            labels.style.height = `${form.offsetHeight}px`;
            obs.disconnect();
          }
        });
      });

      observer.observe(kiwi, { subtree: true, childList: true });
    }
  }

  const lightbox = node.querySelector<HTMLElement>('[data-lightbox]');
  const carousel = node.querySelector<HTMLElement>('[data-carousel]');
  const imageWrapper = node.querySelector<HTMLElement>('[data-images]');
  const lightboxImages = node.querySelector<HTMLElement>('[data-lb-images]');
  const prevArrow = node.querySelector<HTMLElement>('[data-prev-arrow]');
  const nextArrow = node.querySelector<HTMLElement>('[data-next-arrow]');

  if (!lightbox || !carousel || !imageWrapper || !lightboxImages) return;

  let slider: SwiperType | null = null;

  // The position can be passed explicitly when the gallery has just been
  // scrolled back to the start, since the previous Swiper instance still
  // reports the scroll position it had before the images were replaced.
  //
  // The arrows are hidden with `visibility` rather than `display`, so their
  // animations keep running while hidden and the two stay in sync.
  const updateArrows = (
    { atStart, atEnd } = { atStart: slider?.isBeginning ?? true, atEnd: slider?.isEnd ?? false },
  ) => {
    const singleSlide = node.querySelectorAll('[data-image]').length < 2;

    prevArrow?.classList.toggle('invisible', singleSlide || atStart);
    nextArrow?.classList.toggle('invisible', singleSlide || atEnd);
  };

  const initSlider = async () => {
    if (!window.matchMedia('(min-width: 1024px)').matches) {
      const { default: Swiper, Scrollbar } = await import(
        /* webpackChunkName: "pdp-images-swiper" */ 'modules/swiper'
      );

      if (slider) {
        slider.destroy(true, true);
      }

      slider = new Swiper(carousel, {
        modules: [Scrollbar],
        slidesPerView: 'auto',
        cssMode: true,
        init: false,
        scrollbar: {
          el: '[data-thumbnails-scrollbar]',
        },
      });

      slider.on('setTranslate', () => updateArrows());

      slider.init();
      updateArrows();
    }
  };

  const init = () => {
    lightbox.addEventListener('open', () => {
      node.style.zIndex = '61';
      node.classList.remove('transform-gpu');
    });

    lightbox.addEventListener('close', () => {
      node.classList.add('transform-gpu');
      node.style.zIndex = '';
    });

    prevArrow?.addEventListener('click', () => slider?.slidePrev());
    nextArrow?.addEventListener('click', () => slider?.slideNext());

    initSlider();
  };

  init();

  emitter.on('product:media:updated', (event: unknown) => {
    const { media } = event as { media: Media[] };

    clear(imageWrapper);
    imageWrapper.innerHTML = media
      .map((item, index) => {
        let orderIndex: number;
        switch (index) {
          case 0:
            orderIndex = 1;
            break;
          case 1:
            orderIndex = 2;
            break;
          case 2:
            orderIndex = 0;
            break;
          default:
            orderIndex = index;
            break;
        }

        return `<div class="swiper-slide w-full shrink-0 snap-start snap-always lg:h-auto lg:order-[var(--order)] ${
          media.length === 1 ? '' : 'lg:w-[calc(50%-0.125rem)]'
        }" data-image="${index}" style="--order: ${orderIndex}">
      <div class="aspect-w-3 aspect-h-4">
      ${renderImage(item)}
      </div>
      </div>`;
      })
      .join('');

    clear(lightboxImages);
    lightboxImages.innerHTML = media
      .map(
        (item) => `<div
          data-lb-image
          class="aspect-w-1"
          style="--tw-aspect-h: ${1 / item.aspect_ratio}"
        >
        ${renderImage(item)}
        </div>`,
      )
      .join('');

    // The wrapper has `scroll-behavior: smooth` below 1024px, so `instant` is
    // needed to jump back to the first image without animating.
    imageWrapper.scrollTo({ left: 0, behavior: 'instant' });

    updateArrows({ atStart: true, atEnd: false });

    initSlider();
  });
};

export default component;
